// packages/ui-kit/src/primitives/Chip.tsx
// C3F v18 — reference: design/system/components/core/Chip.{jsx,prompt.md}
// Filter / multi-selection pill, 32px tall. Different from Badge: the chip is
// TOUCHED, the badge is read (and Tag is the squared, silent metadata label).
import { type KeyboardEvent, type ReactNode } from "react";

export type ChipTone = "neutral" | "info" | "success" | "warning" | "danger" | "accent";
type LegacyVariant = "soft" | "solid" | "outline";
type LegacySize = "xs" | "sm";

interface Props {
  /** Selected filter: fills with the theme ink (--fg-default / --fg-inverse). */
  selected?: boolean;
  onClick?: () => void;
  onRemove?: () => void;
  iconLeft?: ReactNode;
  children: ReactNode;
  className?: string;
  /** @deprecated pre-v18: state-tinted chips are Badges' job; kept so existing
   *  call sites (availability rules, channel toggles) keep their meaning. */
  tone?: ChipTone;
  /** @deprecated pre-v18; `solid` renders as `selected`. */
  variant?: LegacyVariant;
  /** @deprecated pre-v18 compact sizes; the DS chip is one 32px size. */
  size?: LegacySize;
}

// Legacy tinted looks (opaque soft tints paired ONLY with their own ink;
// neutral rests on --bg-subtle, never a hover/active veil).
const legacySoftCls: Record<ChipTone, string> = {
  neutral: "bg-subtle text-fg-default border-transparent",
  info: "bg-info-soft text-info border-transparent",
  success: "bg-success-soft text-success border-transparent",
  warning: "bg-warning-soft text-warning border-transparent",
  danger: "bg-danger-soft text-danger border-transparent",
  accent: "bg-accent-soft text-accent-text border-transparent",
};
const legacyOutlineCls: Record<ChipTone, string> = {
  neutral: "border-border-control text-fg-default",
  info: "border-info-strong text-info",
  success: "border-success-strong text-success",
  warning: "border-warning-strong text-warning",
  danger: "border-danger-strong text-danger",
  accent: "border-accent-strong text-accent-text",
};
const legacySolidCls: Record<ChipTone, string> = {
  neutral: "bg-fg-default text-fg-inverse border-transparent",
  info: "bg-info text-fg-inverse border-transparent",
  success: "bg-success text-fg-inverse border-transparent",
  warning: "bg-warning text-fg-inverse border-transparent",
  danger: "bg-danger text-fg-inverse border-transparent",
  accent: "bg-accent-text text-fg-inverse border-transparent",
};

// DS: one 32px pill; legacy compact sizes stay on the closed type scale.
const sizeCls: Record<LegacySize | "md", string> = {
  xs: "h-6 px-2.5 text-xs",
  sm: "h-7 px-3 text-caption",
  md: "h-8 px-3 text-sm",
};

export function Chip({
  selected,
  onClick,
  onRemove,
  iconLeft,
  children,
  className = "",
  tone = "neutral",
  variant,
  size,
}: Props) {
  const interactive = !!onClick;
  const isSelected = selected ?? (variant === "solid" && tone === "neutral");

  let look: string;
  if (isSelected) {
    // Inversion inside a theme-following surface uses the THEME pair
    // (--fg-default / --fg-inverse), which swaps by itself — never the fixed
    // praesto-night/paper brand pair (build.md).
    look = "bg-fg-default text-fg-inverse border-transparent";
  } else if (variant === "solid") {
    look = legacySolidCls[tone];
  } else if (variant === "soft") {
    look = legacySoftCls[tone];
  } else {
    // DS default: outline on --border-control (it's a control, WCAG 1.4.11).
    look = legacyOutlineCls[tone];
  }

  const hoverCls = interactive && !isSelected ? "hover:bg-hover active:bg-active" : "";
  // Touch floor: interactive chips keep 44px on touch viewports and drop to
  // the dense DS height at the sm: breakpoint (same convention as Button sm).
  const touchCls = interactive ? "min-h-touch sm:min-h-0" : "";

  const onKeyDown = interactive
    ? (e: KeyboardEvent<HTMLSpanElement>) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick();
        }
      }
    : undefined;

  return (
    <span
      {...(interactive
        ? { role: "button" as const, tabIndex: 0, onClick, onKeyDown, "aria-pressed": isSelected }
        : {})}
      className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-pill border font-sans font-medium transition-colors duration-fast ${
        interactive ? "cursor-pointer focus-visible:outline-none focus-visible:shadow-focus" : ""
      } ${look} ${hoverCls} ${sizeCls[size ?? "md"]} ${touchCls} ${className}`}
    >
      {iconLeft}
      {children}
      {onRemove && (
        <button
          type="button"
          aria-label="Rimuovi"
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
          className="-mr-1 inline-flex h-7 w-7 cursor-pointer items-center justify-center rounded-full text-sm leading-none transition-colors duration-fast hover:bg-hover focus-visible:outline-none focus-visible:shadow-focus sm:h-5 sm:w-5"
        >
          &times;
        </button>
      )}
    </span>
  );
}
